import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAPI } from '../../../api';
import PageSection from '../../../components/PageSection';
import RawJsonDisplay from '../../../components/RawJsonDisplay';
import PropEditor from '../../../components/PropEditor';
import DataTable from '../../../components/DataTable';
import { useAuth } from '../../../auth/AuthProvider';

const BranchUser = ({branch}) => {
	const auth = useAuth();
	const api = useAPI();
	const navigate = useNavigate();

	const { userID } = useParams();

	const [branches, setBranches] = useState([]);
	const [userInfo, setUserInfo] = useState({});
	const [accessLevels, setAccessLevels] = useState([]);

	const [setAccessGrantState, setSetAccessGrantState] = useState({ branch: '', accessLevel: '', groups: [], classes: [] });

	const [accessGrantUserGroups, setAccessGrantUserGroups] = useState([]);
	//const [accessGrantClasses, setAccessGrantClasses] = useState([]);
	const [accessGrantCategories, setAccessGrantCategories] = useState([]);

	const loadUser = () => {
		api.get(`/api/branch/${branch.id}/admin/users/` + userID)
		.then(json => setUserInfo(json))
		.catch(err => {
			console.error(err);
			navigate(`/${branch.id}/users`);
		});
	};

	const loadBranches = () => {
		setBranches(auth.profile.branches);
	};

	const loadAccessLevels = () => {
		api.get(`/api/branch/${branch.id}/admin/access`)
		.then(json => setAccessLevels(json));
	};

	const loadAccessGrantUserGroups = () => {
		if (setAccessGrantState.branch !== '') {
			api.get(`/api/branch/${setAccessGrantState.branch}/firewall/usergroups`)
			.then(json => setAccessGrantUserGroups(json));
		} else {
			setAccessGrantUserGroups([]);
		}
	};

	const loadAccessGrantClasses = () => {
		// if (setAccessGrantState.branch !== '') {
		// 	// api.get(`/api/branch/${setAccessGrantState.branch}/firewall/classes`)
		// 	// .then(json => setAccessGrantClasses(json));
		// 	setAccessGrantClasses([]);
		// } else {
		// 	setAccessGrantClasses([]);
		// }
	};

	const loadAccessGrantCategories = () => {
		if (setAccessGrantState.branch !== '') {
			api.get(`/api/branch/${setAccessGrantState.branch}/firewall/categories`)
			.then(json => setAccessGrantCategories(json));
		} else {
			setAccessGrantCategories([]);
		}
	};

	useEffect(() => {
		loadUser();
		loadBranches();
		loadAccessLevels();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		loadAccessGrantUserGroups();
		loadAccessGrantClasses();
		loadAccessGrantCategories();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [setAccessGrantState.branch]);

	function updateVerifyAddAccessGrantState(newState) {
		setSetAccessGrantState(newState);
	};

	function setUserAccessGrant() {
		if (setAccessGrantState.branch === '' || setAccessGrantState.accessLevel === '')
			return alert('Select branch and access level first');
		
		api.put(`/api/branch/${branch.id}/admin/users/${userID}/access/${setAccessGrantState.branch}`, { 
			accessLevel: setAccessGrantState.accessLevel,
			groups: setAccessGrantState.groups,
			classes: setAccessGrantState.classes
		})
		.then(loadUser);
	};

	function deleteAccessGrant(branchID) {
		let accessGrant = userInfo.access.find(ag => ag.branch === branchID);

		let branchName = accessGrant.branch ? branches.find(branch => branch.id === accessGrant.branch)?.name ?? accessGrant.branch : '(none)';
		let accessLevelName = accessLevels.find(accessLevel => accessLevel.id === accessGrant.accessLevel)?.name ?? accessGrant.accessLevel;

		let grantDescription = `${branchName} (${accessLevelName})`;

		if (accessGrant && window.confirm(`Delete access grant ${grantDescription})?`)) {
			api.delete(`/api/branch/${branch.id}/admin/users/${userID}/access/${branchID}`)
			.then(loadUser);
		}
	}

	return (
		<div>
			<PageSection title={`User: ${userInfo && userInfo.username}`} level={2} />

			<PageSection>
				<Link to={`/${branch.id}/users`}>&#x25c0; Back to Users</Link>
			</PageSection>

			<PageSection title="User Info" level={3}>
				<PropEditor 
					object={userInfo}
					onChange={setUserInfo}
					props={[
						{ label: 'Display Name', key: 'display' },
						{ label: 'Username', key: 'username', disabled: true },
					]}
				/>
			</PageSection>

			<PageSection title="Access Grants" level={3}>
				<DataTable
					data={userInfo.access}
					columns={[
						{ label: 'Branch', key: 'branch', render: (access) => access.branch ? branches.find(branch => branch.id === access.branch)?.name ?? access.branch : '(none)' },
						{ label: 'Access Level', key: 'accessLevel', render: (access) => accessLevels.find(accessLevel => accessLevel.id === access.accessLevel)?.name ?? access.accessLevel },
						{ label: 'Categories', key: 'categories', render: (access) => access.categories ? access.categories.length > 0 ? access.categories.join(', ') : '(empty)' : '(null)' },
						{ label: 'Groups', key: 'groups', render: (access) => access.groups ? access.groups.length > 0 ? access.groups.join(', ') : '(empty)' : '(null)' },
						{ label: 'Classes', key: 'classes', render: (access) => access.classes ? access.classes.length > 0 ? access.classes.join(', ') : '(empty)' : '(null)' },
					]}
					tools={[
						{ label: 'Edit', action: (access) => setSetAccessGrantState({ branch: access.branch, accessLevel: access.accessLevel, categories: access.categories, groups: access.groups, classes: access.classes }) },
						{ label: 'Delete', render: (access) => <button className="link" onClick={() => deleteAccessGrant(access.branch)}>Delete</button> }
					]}
				/>
			</PageSection>

			<PageSection title="Raw Data" level={3}>
				<RawJsonDisplay data={userInfo} />
			</PageSection>

			<PageSection title="Grant Access" level={4}>
				<PropEditor
					object={setAccessGrantState}
					onChange={updateVerifyAddAccessGrantState}
					props={
						[
							{
								label: 'Branch',
								key: 'branch',
								type: 'select',
								options: [{ label: '(none)', value: '' }].concat(branches.map(branch => ({ label: branch.name, value: branch.id })))
							},
							{
								label: 'Access Permission',
								key: 'accessLevel',
								type: 'select',
								options: [{ label: '(none)', value: '' }].concat(accessLevels?.map(accessLevel => ({ label: accessLevel.name, value: accessLevel.id })))
							},
							{
								label: 'Categories',
								key: 'categories',
								type: 'multiselect',
								options: accessGrantCategories.map(category => ({ label: category.name, value: category.id }))
							},
							{
								label: 'Groups',
								key: 'groups',
								type: 'multiselect',
								options: accessGrantUserGroups.map(group => ({ label: group.name, value: group.id }))
							},
							{
								label: 'Classes',
								key: 'classes',
								type: 'text',
								placeholder: 'Class1, Class2, Class3',
								present: (value) => value?.join(', ') ?? '',
								parse: (value) => value.split(',').map(v => v.trim())
							}
						]
					}
					tools={[
						() => <input type="button" value="Grant Access" onClick={setUserAccessGrant} />
					]}
				/>
			</PageSection>

			<PageSection title="Grant Access Raw Data" level={3}>
				<RawJsonDisplay data={setAccessGrantState} />
			</PageSection>
		</div>
	);
};

export default BranchUser;